import { Component, OnInit, inject } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormTemplateService } from 'src/app/Services/Forms/form-template.service';
import { UserDataService } from 'src/app/Services/Authentication/User/user-data.service';
import { Form_Template_Field, Form_Template_With_Fields, Response_Forms } from 'src/app/Interfaces/Forms/FormTemplate.interface';
import { GeneralRoleCDMS, typeSystemRoleCDMS } from 'src/app/types/General/RoleCDMS.type';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-form-builder-cdms',
  templateUrl: './form-builder-cdms.component.html',
  styleUrls: ['./form-builder-cdms.component.scss', '../../../Styles/GlobalStyles.css'],
  standalone: false,
  animations: [
    trigger('slideEditor', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)', maxHeight: '0' }),
        animate('280ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0)', maxHeight: '55%' }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(20px)', maxHeight: '0' }))
      ])
    ])
  ]
})
export class FormBuilderCDMSComponent implements OnInit {

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formTemplateService = inject(FormTemplateService);
  public UserService = inject(UserDataService);
  public RolesGeneral: typeSystemRoleCDMS = GeneralRoleCDMS;

  public IsEdit: boolean = false;
  public TemplateId: number | null = null;
  public Template: Form_Template_With_Fields | null = null;

  // Form properties
  public TemplateName: string = '';
  public TemplateDescription: string = '';
  public TemplateCategory: string = '';

  // Fields
  public Fields: Form_Template_Field[] = [];
  public SelectedField: Form_Template_Field | null = null;
  public ShowFieldEditor: boolean = false;

  // Field editor form
  public FieldForm: FormGroup = new FormGroup({
    Label: new FormControl('', Validators.required),
    Field_Type: new FormControl('text_short', Validators.required),
    Placeholder: new FormControl(''),
    Required: new FormControl(false),
    Default_Value: new FormControl(''),
    Options: new FormControl(''),
    Section_Name: new FormControl(''),
    Val_MinLength: new FormControl(null),
    Val_MaxLength: new FormControl(null),
    Val_Pattern: new FormControl(''),
    Val_Min: new FormControl(null),
    Val_Max: new FormControl(null)
  });

  public FieldTypes = [
    { label: 'Texto corto', value: 'text_short', icon: 'fa-font' },
    { label: 'Texto largo', value: 'text_long', icon: 'fa-align-left' },
    { label: 'Texto Enriquecido', value: 'rich_text', icon: 'fa-pen-nib' },
    { label: 'Numero', value: 'number', icon: 'fa-hashtag' },
    { label: 'Fecha', value: 'date', icon: 'fa-calendar' },
    { label: 'Hora', value: 'time', icon: 'fa-clock' },
    { label: 'Email', value: 'email', icon: 'fa-envelope' },
    { label: 'Teléfono', value: 'phone', icon: 'fa-phone' },
    { label: 'URL', value: 'url', icon: 'fa-link' },
    { label: 'Seleccion unica', value: 'select', icon: 'fa-caret-down' },
    { label: 'Seleccion multiple', value: 'multi_select', icon: 'fa-list-check' },
    { label: 'Lista de casillas de verificación', value: 'checkbox_list', icon: 'fa-check-square' },
    { label: 'Si/No', value: 'boolean', icon: 'fa-toggle-on' },
    { label: 'Imagen', value: 'image', icon: 'fa-image' },
    { label: 'Archivo', value: 'file', icon: 'fa-paperclip' },
    { label: 'Código QR', value: 'qr_code', icon: 'fa-qrcode' },
    { label: 'Código de barras', value: 'barcode', icon: 'fa-barcode' },
    { label: 'Tabla', value: 'table', icon: 'fa-table-cells' },
    { label: 'Firma', value: 'signature', icon: 'fa-signature' },
    { label: 'Seccion/Separador', value: 'section', icon: 'fa-grip-lines' }
  ];

  private ColorConfirm: string = '#20a8e1';
  private ColorsCancel: string = '#fc6161';

  constructor() {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.TemplateId = Number(idParam);
      this.IsEdit = true;
      this.LoadTemplate();
    }
  }

  public CanPublishTemplate(): boolean {
    return this.UserService.UserRoleGeneralIn([
      this.RolesGeneral.FormTemplatesAdmin,
      this.RolesGeneral.FormTemplatesUser,
      this.RolesGeneral.AdministratorFIN
    ]);
  }

  private LoadTemplate(): void {
    if (!this.TemplateId) return;
    this.formTemplateService.GetFormTemplateById(this.TemplateId).subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          this.Template = res.operation;
          this.TemplateName = this.Template!.Name;
          this.TemplateDescription = this.Template!.Description || '';
          this.TemplateCategory = this.Template!.Category || '';
          this.Fields = this.Template!.Fields.map(f => ({ ...f, UI_ID: f.UI_ID || Math.random().toString(36).substring(2, 9) }));
        }
      },
      error: () => {
        Swal.fire('Error', 'No se pudo cargar el formulario.', 'error');
      }
    });
  }

  // ======================== FIELD MANAGEMENT ========================

  public AddField(): void {
    this.SelectedField = null;
    this.FieldForm.reset({
      Label: '',
      Field_Type: 'text_short',
      Placeholder: '',
      Required: false,
      Default_Value: '',
      Options: '',
      Section_Name: ''
    });
    this.TableColumns = [{ name: '', type: 'text_short' }];
    this.ShowFieldEditor = true;
  }

  public EditField(field: Form_Template_Field, index: number): void {
    this.SelectedField = field;

    let valMin = null, valMax = null, valMinLen = null, valMaxLen = null, valPattern = '';
    if (field.Validations) {
      try {
        const v = typeof field.Validations === 'string' ? JSON.parse(field.Validations) : field.Validations;
        valMinLen = v.minLength || null;
        valMaxLen = v.maxLength || null;
        valPattern = v.pattern || '';
        valMin = v.min !== undefined ? v.min : null;
        valMax = v.max !== undefined ? v.max : null;
      } catch (e) {}
    }

    let optionsStr = '';
    if (field.Options) {
      if (field.Field_Type === 'table') {
        // For tables, keep the raw JSON — InitTableColumns will parse it
        optionsStr = field.Options;
      } else {
        try {
          const opts = typeof field.Options === 'string' ? JSON.parse(field.Options) : field.Options;
          optionsStr = Array.isArray(opts) ? opts.join('\n') : '';
        } catch (e) {
          optionsStr = field.Options;
        }
      }
    }

    this.FieldForm.patchValue({
      Label: field.Label,
      Field_Type: field.Field_Type,
      Placeholder: field.Placeholder || '',
      Required: field.Required,
      Default_Value: field.Default_Value || '',
      Options: optionsStr,
      Section_Name: field.Section_Name || '',
      Val_MinLength: valMinLen,
      Val_MaxLength: valMaxLen,
      Val_Pattern: valPattern,
      Val_Min: valMin,
      Val_Max: valMax
    });
    if (field.Field_Type === 'table') {
      this.InitTableColumns();
    } else {
      this.TableColumns = [{ name: '', type: 'text_short' }];
    }
    this.ShowFieldEditor = true;
  }

  public SaveField(): void {
    if (this.FieldForm.get('Label')?.invalid) {
      Swal.fire('Error', 'El campo Label es obligatorio.', 'warning');
      return;
    }

    const formVal = this.FieldForm.value;

    // Sync table columns before saving
    if (formVal.Field_Type === 'table') {
      this.SyncTableColumnsToOptions();
    }

    let options: string | null = null;
    if (formVal.Field_Type === 'table') {
      // For tables, Options is already JSON from SyncTableColumnsToOptions
      const cols = this.TableColumns.filter(c => c.name.trim() !== '');
      if (cols.length > 0) {
        options = JSON.stringify(cols);
      }
    } else if (formVal.Options && formVal.Options.trim()) {
      const optArray = formVal.Options.split('\n').map((o: string) => o.trim()).filter((o: string) => o);
      options = JSON.stringify(optArray);
    }

    let validations: string | null = null;
    const valObj: any = {};
    if (formVal.Val_MinLength) valObj.minLength = Number(formVal.Val_MinLength);
    if (formVal.Val_MaxLength) valObj.maxLength = Number(formVal.Val_MaxLength);
    if (formVal.Val_Pattern) valObj.pattern = formVal.Val_Pattern;
    if (formVal.Val_Min !== null && formVal.Val_Min !== '') valObj.min = Number(formVal.Val_Min);
    if (formVal.Val_Max !== null && formVal.Val_Max !== '') valObj.max = Number(formVal.Val_Max);
    if (Object.keys(valObj).length > 0) validations = JSON.stringify(valObj);

    const field: Form_Template_Field = {
      UI_ID: this.SelectedField?.UI_ID || Math.random().toString(36).substring(2, 9),
      Label: formVal.Label,
      Field_Type: formVal.Field_Type,
      Placeholder: formVal.Placeholder || null,
      Required: formVal.Required || false,
      Default_Value: formVal.Default_Value || null,
      Options: options,
      Validations: validations,
      Field_Order: 0,
      Section_Name: formVal.Section_Name || null
    };

    if (this.SelectedField) {
      const index = this.Fields.indexOf(this.SelectedField);
      if (index >= 0) {
        field.Field_Order = this.SelectedField.Field_Order;
        field.ID = this.SelectedField.ID;
        this.Fields[index] = field;
        this.Fields = [...this.Fields];
      }
    } else {
      field.Field_Order = this.Fields.length + 1;
      this.Fields.push(field);
      this.Fields = [...this.Fields];
    }

    this.ShowFieldEditor = false;
    this.SelectedField = null;
  }

  public RemoveField(index: number): void {
    this.Fields.splice(index, 1);
    this.ReorderFields();
    this.Fields = [...this.Fields];
  }

  public DropField(event: CdkDragDrop<Form_Template_Field[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.Fields, event.previousIndex, event.currentIndex);
    this.ReorderFields();
    this.Fields = [...this.Fields];
  }

  private ReorderFields(): void {
    this.Fields.forEach((f, i) => f.Field_Order = i + 1);
  }

  public GetFieldTypeLabel(type: string): string {
    const found = this.FieldTypes.find(ft => ft.value === type);
    return found ? found.label : type;
  }

  public GetFieldTypeIcon(type: string): string {
    const found = this.FieldTypes.find(ft => ft.value === type);
    return found ? `fa-solid ${found.icon}` : 'fa-solid fa-question';
  }

  // ======================== TABLE COLUMN MANAGEMENT ========================

  public TableColumns: { name: string, type: string }[] = [];

  public ColumnTypeOptions = [
    { label: 'Texto', value: 'text_short' },
    { label: 'Numero', value: 'number' },
    { label: 'Si/No', value: 'boolean' },
    { label: 'Seleccion', value: 'select' }
  ];

  public InitTableColumns(): void {
    const optionsVal = this.FieldForm.get('Options')?.value;
    if (optionsVal) {
      try {
        const parsed = typeof optionsVal === 'string' ? JSON.parse(optionsVal) : optionsVal;
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].name) {
          this.TableColumns = parsed;
          return;
        }
      } catch (e) {}
    }
    this.TableColumns = [{ name: '', type: 'text_short' }];
  }

  public AddTableColumn(): void {
    this.TableColumns.push({ name: '', type: 'text_short' });
  }

  public RemoveTableColumn(index: number): void {
    if (this.TableColumns.length > 1) {
      this.TableColumns.splice(index, 1);
    }
  }

  public SyncTableColumnsToOptions(): void {
    const valid = this.TableColumns.filter(c => c.name.trim() !== '');
    this.FieldForm.get('Options')?.setValue(JSON.stringify(valid));
  }

  // ======================== SAVE / PUBLISH ========================

  public SaveAsDraft(): void {
    this.SaveTemplate(1);
  }

  public PublishTemplate(): void {
    Swal.fire({
      title: '¿Publicar formulario?',
      text: 'El formulario estara disponible para ser usado en procesos.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: this.ColorConfirm,
      cancelButtonColor: this.ColorsCancel,
      confirmButtonText: 'Si, publicar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        // Always save all content + set status 2 in one call (avoids losing unsaved changes)
        this.SaveTemplate(2);
      }
    });
  }

  private SaveTemplate(status: number): void {
    if (!this.TemplateName.trim()) {
      Swal.fire('Error', 'El nombre del formulario es obligatorio.', 'warning');
      return;
    }
    if (this.Fields.length === 0) {
      Swal.fire('Error', 'Agrega al menos un campo al formulario.', 'warning');
      return;
    }

    this.ReorderFields();

    const data = {
      Name: this.TemplateName,
      Description: this.TemplateDescription || null,
      Category: this.TemplateCategory || null,
      Status: status,
      Fields: this.Fields.map(f => ({
        Label: f.Label,
        Field_Type: f.Field_Type,
        Placeholder: f.Placeholder,
        Required: f.Required,
        Default_Value: f.Default_Value,
        Options: f.Options,
        Validations: f.Validations,
        Field_Order: f.Field_Order,
        Section_Name: f.Section_Name
      }))
    };

    const request$ = this.IsEdit && this.TemplateId
      ? this.formTemplateService.UpdateFormTemplate(this.TemplateId, data)
      : this.formTemplateService.CreateFormTemplate(data);

    request$.subscribe({
      next: (res: Response_Forms) => {
        if (res.success) {
          const finalId = res.operation?.ID || this.TemplateId;
          
          // El backend puede forzar el estado (siempre borrador al crear, o siempre activo si era una versión activa).
          // Por seguridad, aseguramos que el estado exacto (1 o 2) que el usuario pidió se aplique al ID devuelto por el backend.
          this.formTemplateService.ChangeStatusFormTemplate(finalId, status).subscribe({
            next: () => {
              const msgTitle = status === 2 ? 'Publicado' : 'Guardado';
              const msgText = status === 2 
                 ? 'El formulario ha sido publicado correctamente.' 
                 : 'El formulario se ha guardado como borrador correctamente.';
              
              Swal.fire(msgTitle, msgText, 'success');
              this.router.navigate(['/Dashboard/General/FormTemplates']);
            },
            error: () => Swal.fire('Error', 'Se guardó, pero hubo un error al cambiar su estado.', 'error')
          });
        }
      },
      error: () => {
        Swal.fire('Error', 'No se pudo guardar el formulario.', 'error');
      }
    });
  }

  public GoBack(): void {
    this.router.navigate(['/Dashboard/General/FormTemplates']);
  }

  public CancelFieldEditor(): void {
    this.ShowFieldEditor = false;
    this.SelectedField = null;
  }
}
